import type { AuthChangeEvent, Session, Subscription } from "@supabase/supabase-js";
import { supabase } from "@/data/supabase/client";
import { accountKindSchema } from "@/domain/user/schema";
import type { AccountKind } from "@/domain/user/types";

/**
 * Auth repo — wraps Supabase Auth + the `validate-access-code` edge function.
 *
 * Returns plain Result-shaped objects (`{ error: Error | null }`) rather
 * than throwing. Lets callers (mutations, hooks, AuthContext) decide how
 * to surface failures.
 */

export interface AuthResult {
  error: Error | null;
}

export const authRepo = {
  async signIn(email: string, password: string): Promise<AuthResult> {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: (error as Error | null) ?? null };
  },

  async signUp(
    email: string,
    password: string,
    accessCode: string,
    termsVersion: string,
  ): Promise<AuthResult> {
    // Bad codes never reach auth.signUp, and a good one records the redemption
    // the database reads to make this account a partner.
    const validation = await this.validateAccessCode(accessCode, email, termsVersion);
    if (validation.error) return validation;

    const redirectTo = `${window.location.origin}/auth/confirm`;
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: { emailRedirectTo: redirectTo },
    });
    return { error: (error as Error | null) ?? null };
  },

  async validateAccessCode(
    accessCode: string,
    email: string,
    termsVersion: string,
  ): Promise<AuthResult> {
    const { data, error } = await supabase.functions.invoke("validate-access-code", {
      body: { accessCode, email, termsVersion },
    });
    if (error) {
      return { error: new Error("Nepodařilo se ověřit přístupový kód") };
    }
    if (!data?.valid) {
      return { error: new Error("Neplatný přístupový kód") };
    }
    return { error: null };
  },

  async resetPassword(email: string): Promise<AuthResult> {
    const redirectTo = `${window.location.origin}/auth/reset-password`;
    const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo });
    return { error: (error as Error | null) ?? null };
  },

  async updatePassword(password: string): Promise<AuthResult> {
    const { error } = await supabase.auth.updateUser({ password });
    return { error: (error as Error | null) ?? null };
  },

  async signOut(): Promise<AuthResult> {
    const { error } = await supabase.auth.signOut();
    return { error: (error as Error | null) ?? null };
  },

  /** An account older than the column has no kind, and no kind is not a customer
   *  (ADR 0011). A read that fails answers the same way rather than rejecting, so
   *  a partner is never stranded waiting for an answer that never comes. */
  async getAccountKind(userId: string): Promise<AccountKind | null> {
    try {
      const { data } = await supabase
        .from("profiles")
        .select("account_kind")
        .eq("id", userId)
        .maybeSingle();

      const kind = accountKindSchema.safeParse(data?.account_kind);
      return kind.success ? kind.data : null;
    } catch {
      return null;
    }
  },

  async getSession() {
    const { data } = await supabase.auth.getSession();
    return data.session;
  },

  onAuthStateChange(
    handler: (event: AuthChangeEvent, session: Session | null) => void,
  ): { data: { subscription: Subscription } } {
    return supabase.auth.onAuthStateChange(handler);
  },
};
